import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { History, RefreshCcw, Filter, Trash2, AlertTriangle, User, Globe, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ACTION_BADGE = {
    create: { cls: "bg-emerald-100 text-emerald-800", label: "DODANO" },
    update: { cls: "bg-amber-100 text-amber-800", label: "EDYCJA" },
    delete: { cls: "bg-red-100 text-red-800", label: "USUNIĘTO" },
    login: { cls: "bg-blue-100 text-blue-800", label: "ZALOGOWANIE" },
    login_failed: { cls: "bg-rose-100 text-rose-800", label: "NIEUDANE LOG." },
    status_change: { cls: "bg-violet-100 text-violet-800", label: "ZMIANA STATUSU" },
    purge_all: { cls: "bg-gray-900 text-white", label: "PURGE" },
    purge_old: { cls: "bg-gray-700 text-white", label: "PURGE STARYCH" },
};

const ENTITY_LABEL = {
    announcement: "Komunikat",
    news: "Wiadomość",
    resolution: "Uchwała",
    listing: "Ogłoszenie",
    poll: "Sonda",
    map_poi: "Mapa: punkt",
    map_building: "Mapa: budynek",
    map_zone: "Mapa: strefa",
    map_path: "Mapa: ścieżka",
    report: "Zgłoszenie usterki",
    settings: "Ustawienia",
    user: "Użytkownik",
    auth: "Logowanie",
    audit_log: "Historia",
};

export default function AuditAdmin() {
    const [data, setData] = useState({ items: [], total: 0, entities: [], actions: [] });
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ entity: "", action: "", user_email: "" });
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.entity) params.set("entity", filters.entity);
            if (filters.action) params.set("action", filters.action);
            if (filters.user_email) params.set("user_email", filters.user_email);
            params.set("limit", "200");
            const r = await api.get(`/audit?${params}`);
            setData(r.data || { items: [], total: 0, entities: [], actions: [] });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się załadować historii.");
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const purgeOld = async () => {
        const days = window.prompt("Usunąć wpisy starsze niż ile dni? (np. 90)", "90");
        if (!days) return;
        const n = parseInt(days);
        if (isNaN(n) || n < 1) { toast.error("Podaj liczbę dni ≥ 1"); return; }
        try {
            const r = await api.delete(`/audit?older_than_days=${n}`);
            toast.success(`Usunięto ${r.data.deleted} wpisów`);
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const purgeAll = async () => {
        if (!window.confirm("Usunąć CAŁY dziennik historii? Tej operacji nie da się cofnąć.")) return;
        if (!window.confirm("Na pewno? Stracisz wszystkie wpisy historii działań.")) return;
        try {
            const r = await api.delete(`/audit?older_than_days=0`);
            toast.success(`Usunięto ${r.data.deleted} wpisów`);
            load();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <AdminCard>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <History className="text-[#0D4F3C]" />
                    <div>
                        <h2 className="font-serif text-2xl text-[#092E22]">Historia działań</h2>
                        <p className="text-xs text-gray-500">Wszystkie zmiany w CMS — kto, kiedy i co zmienił.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={load} variant="outline" size="sm" className="rounded-full" disabled={loading} data-testid="audit-refresh">
                        <RefreshCcw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Odśwież
                    </Button>
                    <Button onClick={purgeOld} variant="outline" size="sm" className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="audit-purge-old">
                        <Trash2 size={12} className="mr-1" /> Usuń stare
                    </Button>
                    <Button onClick={purgeAll} variant="outline" size="sm" className="rounded-full text-red-700 border-red-200 hover:bg-red-50" data-testid="audit-purge-all">
                        <AlertTriangle size={12} className="mr-1" /> Wyczyść
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-2xl">
                <div className="flex items-center gap-1 text-xs text-gray-500"><Filter size={12} /> Filtry:</div>
                <select value={filters.entity} onChange={(e) => setFilters({ ...filters, entity: e.target.value })} className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="audit-filter-entity">
                    <option value="">Wszystkie obiekty</option>
                    {data.entities.map((e) => <option key={e} value={e}>{ENTITY_LABEL[e] || e}</option>)}
                </select>
                <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="audit-filter-action">
                    <option value="">Wszystkie akcje</option>
                    {data.actions.map((a) => <option key={a} value={a}>{ACTION_BADGE[a]?.label || a}</option>)}
                </select>
                <Input
                    value={filters.user_email}
                    onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
                    placeholder="e-mail użytkownika"
                    className="h-8 text-xs max-w-xs"
                    data-testid="audit-filter-user"
                />
                {(filters.entity || filters.action || filters.user_email) && (
                    <Button onClick={() => setFilters({ entity: "", action: "", user_email: "" })} variant="ghost" size="sm" className="h-8 rounded-full text-xs">Wyczyść filtry</Button>
                )}
                <span className="ml-auto text-xs text-gray-500">{data.total} wpisów</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                            <th className="text-left py-2 pl-2">Data</th>
                            <th className="text-left py-2">Akcja</th>
                            <th className="text-left py-2">Obiekt</th>
                            <th className="text-left py-2">Użytkownik</th>
                            <th className="text-left py-2">Szczegóły</th>
                            <th className="text-left py-2 pr-2">IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.length === 0 && (
                            <tr><td colSpan="6" className="text-center text-gray-400 italic py-8">Brak wpisów w historii.</td></tr>
                        )}
                        {data.items.map((item) => {
                            const badge = ACTION_BADGE[item.action] || { cls: "bg-gray-100 text-gray-700", label: item.action.toUpperCase() };
                            const isExpanded = !!expanded[item.id];
                            const hasDetails = item.details && Object.keys(item.details || {}).length > 0;
                            return (
                                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 align-top" data-testid={`audit-row-${item.id}`}>
                                    <td className="py-3 pl-2 text-[11px] text-gray-500 whitespace-nowrap font-mono">{new Date(item.ts).toLocaleString("pl-PL")}</td>
                                    <td className="py-3"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${badge.cls}`}>{badge.label}</span></td>
                                    <td className="py-3 text-xs">
                                        <div className="font-medium text-[#092E22]">{ENTITY_LABEL[item.entity] || item.entity}</div>
                                        {item.entity_id && <div className="text-[10px] font-mono text-gray-400 truncate max-w-[120px]">{item.entity_id.slice(0, 8)}…</div>}
                                    </td>
                                    <td className="py-3 text-xs">
                                        <div className="flex items-center gap-1 text-[#092E22]">
                                            <User size={11} /> {item.user_email || "—"}
                                        </div>
                                        {item.user_role && <div className="text-[10px] text-gray-400">{item.user_role}</div>}
                                    </td>
                                    <td className="py-3 text-xs text-gray-700">
                                        <div className="max-w-md break-words">{item.summary || "—"}</div>
                                        {hasDetails && (
                                            <button onClick={() => setExpanded({ ...expanded, [item.id]: !isExpanded })} className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline mt-1">
                                                {isExpanded ? <><ChevronUp size={10} /> Zwiń</> : <><ChevronDown size={10} /> Szczegóły</>}
                                            </button>
                                        )}
                                        {isExpanded && hasDetails && (
                                            <pre className="mt-2 p-2 bg-gray-100 rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify(item.details, null, 2)}</pre>
                                        )}
                                    </td>
                                    <td className="py-3 pr-2 text-[11px] text-gray-400 font-mono whitespace-nowrap">
                                        {item.ip && <span className="inline-flex items-center gap-1"><Globe size={10} /> {item.ip}</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </AdminCard>
    );
}
